import {
  addDays,
  addHours,
  differenceInCalendarDays,
  differenceInHours,
  differenceInMinutes,
  isAfter,
  isBefore,
  parseISO,
} from 'date-fns';
import { ReservationStatus } from '@classpass/protobuf-definitions/lib/web/models/partners/reservation';
import { FillRatePace, FillRateSource } from '@classpass/protobuf-definitions/lib/web/models/partners/schedule';

export const DETAIL_TAB = {
  INSIGHTS: 'insights',
  ROSTER: 'roster',
};

export const ROSTER_DEFAULT_WINDOW_MINUTES = 60;

export function getDefaultDetailTab(schedule, now = new Date()) {
  const { isInProgress, isInThePast, startDateTime } = schedule;
  const startsSoon = differenceInMinutes(startDateTime, now) < ROSTER_DEFAULT_WINDOW_MINUTES;

  return isInProgress || isInThePast || startsSoon ? DETAIL_TAB.ROSTER : DETAIL_TAB.INSIGHTS;
}

export const LAST_WEEK_FILL_MAX_AGE_DAYS = 8;

export function getMostRecentFillHistoryEntry(history = [], scheduleStartDateTime) {
  if (!scheduleStartDateTime) {
    return null;
  }

  const entry = [...history]
    .filter(({ scheduleDate } = {}) => scheduleDate)
    .sort((a, b) => parseISO(b.scheduleDate) - parseISO(a.scheduleDate))
    .find(({ scheduleDate }) => {
      const daysBeforeSchedule = differenceInCalendarDays(scheduleStartDateTime, parseISO(scheduleDate));
      return daysBeforeSchedule >= 1 && daysBeforeSchedule <= LAST_WEEK_FILL_MAX_AGE_DAYS;
    });

  return entry && entry.totalReservations != null && entry.maxCapacity != null ? entry : null;
}

export const ACTIONS_WINDOW_HOURS_AFTER_END = 6;
export const EXTENDED_ACTIONS_WINDOW_DAYS_AFTER_END = 14;

export function getActionWindows(schedule, now = new Date()) {
  const { endDateTime } = schedule;
  const actionsWindowEnd = addHours(endDateTime, ACTIONS_WINDOW_HOURS_AFTER_END);
  const extendedActionsWindowEnd = addDays(endDateTime, EXTENDED_ACTIONS_WINDOW_DAYS_AFTER_END);

  return {
    canPerformScheduleActions: isBefore(now, actionsWindowEnd),
    isInExtendedActionsWindow: isAfter(now, actionsWindowEnd) && isBefore(now, extendedActionsWindowEnd),
  };
}

const COUNTABLE_RESERVATION_STATUSES = [ReservationStatus.PROCESSED, ReservationStatus.QUEUED];

export function countReservations(roster = []) {
  return roster.reduce(
    (counts, { reservationStatus, visitNumberToVenue }) => {
      const isCountable = COUNTABLE_RESERVATION_STATUSES.includes(reservationStatus);

      return {
        newReservationsCount: counts.newReservationsCount + (isCountable && visitNumberToVenue === 1 ? 1 : 0),
        reservationsCount: counts.reservationsCount + (isCountable ? 1 : 0),
      };
    },
    { newReservationsCount: 0, reservationsCount: 0 }
  );
}

export const ADD_SPOT_ACTION = {
  ADD_SPOT: 'addSpot',
  OPPORTUNITY: 'opportunity',
};

export function getAddSpotAction(schedule, { hasSpotManagePermission = false, opportunityActedOn = false } = {}) {
  const {
    classpassSpotsUnfilled,
    hasIntegrationData,
    isFull,
    isInThePast,
    isLocked,
    opportunity,
    reservationsStopped,
    smartspotEnabled,
  } = schedule;

  if (!hasSpotManagePermission || isInThePast || isLocked || reservationsStopped) {
    return null;
  }

  const recommendation = opportunity ? opportunity.recommendation : false;
  if (recommendation && !smartspotEnabled && !opportunityActedOn) {
    return { recommendation, type: ADD_SPOT_ACTION.OPPORTUNITY };
  }

  if (smartspotEnabled) {
    return (classpassSpotsUnfilled === 0 || classpassSpotsUnfilled === Infinity) && !isFull
      ? { type: ADD_SPOT_ACTION.ADD_SPOT }
      : null;
  }

  if (!hasIntegrationData) {
    return { type: ADD_SPOT_ACTION.ADD_SPOT };
  }

  return isFull ? null : { type: ADD_SPOT_ACTION.ADD_SPOT };
}

export const FILL_RATE_PACE = {
  AT_OR_ABOVE: FillRatePace.FILL_RATE_PACE_AT_OR_ABOVE,
  BELOW: FillRatePace.FILL_RATE_PACE_BELOW,
};

export const FILL_RATE_SOURCE = {
  FILL_HISTORY: FillRateSource.FILL_RATE_SOURCE_FILL_HISTORY,
};

export const DIRECT_BOOKINGS_COPY = {
  AT_OR_ABOVE: 'atOrAbove',
  BELOW: 'below',
  ERROR: 'error',
  NO_HISTORY: 'noHistory',
};

export const DIRECT_BOOKINGS_CHART_DAYS = 7;
const HOURS_PER_DAY = 24;
const EMPTY_SERIES_MAX = 1;

export function hasFillRateLoadError(fillRate = {}) {
  const { current = [], sourceErrors = [] } = fillRate;

  return current.length === 0 && sourceErrors.some(({ source }) => source === FILL_RATE_SOURCE.FILL_HISTORY);
}

export function getDirectBookingsCopyVariant(fillRate = {}, hasLoadError = false) {
  if (hasLoadError) {
    return DIRECT_BOOKINGS_COPY.ERROR;
  }

  switch (fillRate.reasoning?.pace) {
    case FILL_RATE_PACE.BELOW:
      return DIRECT_BOOKINGS_COPY.BELOW;
    case FILL_RATE_PACE.AT_OR_ABOVE:
      return DIRECT_BOOKINGS_COPY.AT_OR_ABOVE;
    default:
      return DIRECT_BOOKINGS_COPY.NO_HISTORY;
  }
}

function toChartPoint({ daysBefore = 0, directReservations = 0 }) {
  return { daysBefore, x: Number((DIRECT_BOOKINGS_CHART_DAYS - daysBefore).toFixed(2)), y: directReservations };
}

// The backend's first 4-week-average bucket sits a few hours inside the 7-day window, which left the
// line short of the y axis; carrying that first value back to the window start keeps it flush with Current.
function startAtWindowStart(points) {
  const [first] = points;

  return first && first.x > 0 ? [{ daysBefore: DIRECT_BOOKINGS_CHART_DAYS, x: 0, y: first.y }, ...points] : points;
}

function getTodayDaysBefore(scheduleStartDateTime, now) {
  const hoursUntilStart = differenceInHours(scheduleStartDateTime, now);

  return Math.min(DIRECT_BOOKINGS_CHART_DAYS, Math.max(0, hoursUntilStart) / HOURS_PER_DAY);
}

function flatAtZero(fromX, toX) {
  return [
    { daysBefore: DIRECT_BOOKINGS_CHART_DAYS - fromX, x: fromX, y: 0 },
    { daysBefore: DIRECT_BOOKINGS_CHART_DAYS - toX, x: toX, y: 0 },
  ];
}

// With nothing to plot the chart used to render as an empty grid; Figma's "no historical data" state draws
// the empty series flat at zero instead. Current is only flattened when the class really has no direct
// bookings, so a missing curve never reads as zero on a class that does have them.
export function getDirectBookingsChart(
  fillRate = {},
  schedule = {},
  { directBooked, hasLoadError = false, now = new Date() } = {}
) {
  const {
    capacity,
    current = [],
    past4WkAvg = [],
    prediction,
  } = hasLoadError ? { capacity: fillRate.capacity } : fillRate;
  const observedCurrent = current.map(toChartPoint);
  const pastAvgPoints = startAtWindowStart(past4WkAvg.map(toChartPoint));
  const hasHistory = pastAvgPoints.length > 0;
  const latestCurrent = observedCurrent[observedCurrent.length - 1];
  const today = latestCurrent || toChartPoint({ daysBefore: getTodayDaysBefore(schedule.startDateTime, now) });
  const hasStarted = Boolean(schedule.isInProgress || schedule.isInThePast);
  const canDrawEmptySeries = !hasLoadError;
  const currentPoints =
    observedCurrent.length === 0 && canDrawEmptySeries && directBooked === 0 ? flatAtZero(0, today.x) : observedCurrent;
  const predictionPoints =
    prediction?.value != null && hasHistory && !hasStarted
      ? [today, { daysBefore: 0, x: DIRECT_BOOKINGS_CHART_DAYS, y: prediction.value }]
      : [];
  const seriesMax = Math.max(
    EMPTY_SERIES_MAX,
    ...[...currentPoints, ...pastAvgPoints, ...predictionPoints].map(({ y }) => y)
  );

  return {
    current: currentPoints,
    pastAvg: !hasHistory && canDrawEmptySeries ? flatAtZero(0, DIRECT_BOOKINGS_CHART_DAYS) : pastAvgPoints,
    prediction: predictionPoints,
    today,
    yMax: Math.max(capacity ?? 0, seriesMax),
  };
}
